import { SecLoansFlow, SecLoansSource } from '@shared/sec-loans'
import { Language } from '@shared/types'
import {
  ANALYTICS_PRODUCT_NOT_APPLICABLE,
  AnalyticsData,
  AnalyticsService,
  CommonAnalyticsCustomEventName,
  CommonAnalyticsModalName,
  NotAvailable,
  sendCustomDataEventToAnalytics,
  sendCustomEventToAnalytics,
  sendPageViewEventToAnalytics,
} from '@ui-common/analytics'

import { PhaseStep, PhaseStepType, RootStepType, StepType } from '../wizard/wizardSteps'

// Transforms "StepXyzInfo" into "DetailsXyz"
export type AnalyticsPhaseStep<S extends string> = S extends `Step${infer N}`
  ? AnalyticsPhaseStep<`Details${N}`>
  : S extends `${infer N}Info`
    ? AnalyticsPhaseStep<`${N}`>
    : S

export function toAnalyticsPhaseStep<T extends PhaseStepType>(p: T): AnalyticsPhaseStep<T> {
  return p.replace(/^Step/, 'Details').replace(/Info$/g, '') as AnalyticsPhaseStep<T>
}

export type SecLoansPageViewEventName =
  | 'EntranceStep'
  | 'RootStep'
  | 'EntranceRootStep'
  | 'AssistanceStep'
  | 'StatusRootStep'
  | 'CatalogRootStep'
  | 'TheEndRootStep'
  | AnalyticsPhaseStep<PhaseStepType>

export type SecLoansModalViewEventName =
  | 'AbortModal'
  | 'SendAbortModal'
  | 'ConfirmGoBackModal'
  | 'DeleteApplicationModal'
  | 'ApplicationSentModal'
  | 'ErrorPromptModal'
  | 'TooManyLoansModal'
  | CommonAnalyticsModalName

export type SecLoansCustomEventName =
  | 'EditButton'
  | 'ShowAllLoanApplicationsButton'
  | 'GoToApplicationsButton'
  | 'DeleteApplicationButton'
  | 'SelectLoanApplication'
  | 'SelectIfpApplication'
  | 'SelectLoanOffer'
  | 'CreateNewLoanApplication'
  | 'CreateNewLoanApplicationButton'
  | 'CreateNewIfpApplication'
  | 'SubmitApplication'
  | 'ContinueToBookMeeting'
  | 'Exit'
  | CommonAnalyticsCustomEventName

export type SecLoansCustomEventCategory =
  | SecLoansPageViewEventName
  | SecLoansModalViewEventName
  | CommonAnalyticsCustomEventName

export type SecLoansAnalyticsData = {
  readonly sourceChannel?: SecLoansSource
  readonly flow?: SecLoansFlow
  readonly language: Language
  readonly appVersion: string
}

export const analyticsStepToPageViewEventNameMap: Record<StepType, SecLoansPageViewEventName> = {
  [RootStepType.Root]: 'RootStep',
  [RootStepType.Entrance]: 'EntranceRootStep',
  [RootStepType.Assistance]: 'AssistanceStep',
  [RootStepType.Status]: 'StatusRootStep',
  [RootStepType.Catalog]: 'CatalogRootStep',
  [RootStepType.TheEnd]: 'TheEndRootStep',
  ...(Object.fromEntries(PhaseStep.map((v) => [v, toAnalyticsPhaseStep(v)])) as Record<
    PhaseStepType,
    AnalyticsPhaseStep<PhaseStepType>
  >),
}

export class SecLoansAnalyticsService extends AnalyticsService<
  SecLoansCustomEventCategory | StepType,
  SecLoansCustomEventName,
  CommonAnalyticsModalName
> {
  private readonly analyticsData: AnalyticsData

  constructor(readonly data?: SecLoansAnalyticsData) {
    super()
    this.analyticsData = {
      src: data?.sourceChannel ?? NotAvailable,
      flow: data?.flow ?? NotAvailable,
      product: ANALYTICS_PRODUCT_NOT_APPLICABLE,
      version: data?.appVersion ?? NotAvailable,
      ui_lang: data?.language ?? NotAvailable,
    }
  }

  public sendPageViewEvent = (step: StepType): void => {
    sendPageViewEventToAnalytics(this.resolveAnalyticsPage(step), this.analyticsData)
  }

  public sendCustomEvent = (category: SecLoansCustomEventCategory, name: SecLoansCustomEventName): void => {
    sendCustomEventToAnalytics(category, name, this.analyticsData)
  }

  public sendCustomDataEvent = (data: { [key: string]: string }): void => {
    sendCustomDataEventToAnalytics(data)
  }

  public sendModalViewEvent = (modal: SecLoansModalViewEventName | CommonAnalyticsModalName): void => {
    sendPageViewEventToAnalytics(modal, this.analyticsData)
  }

  public sendStepCustomEvent = (step: StepType, name: SecLoansCustomEventName): void => {
    this.sendCustomEvent(this.resolveAnalyticsPage(step), name)
  }

  public sendModalCustomEvent = (category: SecLoansModalViewEventName, name: SecLoansCustomEventName): void => {
    this.sendCustomEvent(category, name)
  }

  resolveAnalyticsPage = (step: StepType): SecLoansPageViewEventName => {
    return analyticsStepToPageViewEventNameMap[step]
  }
}
